import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { formatPrice } from '../../utils/priceFormatter';
import { showSuccess, showError } from '../../utils/toast';
import BottomNav from '../../components/layouts/BottomNav';
import { FaArrowLeft, FaCheckCircle, FaDownload } from 'react-icons/fa';
import { downloadQuotationPDF } from '../../utils/pdfGenerator';

const API_BASE = ((process.env.REACT_APP_BASE_URL || "http://localhost:5000/").replace(/\/$/, "")) + "/";

const QuotationConfirmationPage = () => {
  const { quotationNumber } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        const response = await api.get(`/quotations/public/number/${quotationNumber}`);
        setQuotation(response.data);
      } catch (error) {
        console.error('Error fetching quotation:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (quotationNumber) {
      fetchQuotation();
    }
  }, [quotationNumber]);

  const handleDownload = async () => {
    if (!quotation) return;
    setDownloading(true);

    const tokenSnapshot = localStorage.getItem('token');
    const userSnapshot = localStorage.getItem('user');
    const ownerSnapshot = localStorage.getItem('session_owner');

    try {
      await downloadQuotationPDF(quotation, quotation.items || []);

      // Restore session if the mobile/PWA download flow clears storage.
      if (tokenSnapshot && !localStorage.getItem('token')) {
        localStorage.setItem('token', tokenSnapshot);
      }
      if (userSnapshot && !localStorage.getItem('user')) {
        localStorage.setItem('user', userSnapshot);
      }
      if (ownerSnapshot && !localStorage.getItem('session_owner')) {
        localStorage.setItem('session_owner', ownerSnapshot);
      }

      showSuccess('Quotation downloaded successfully!');
    } catch (error) {
      console.error('Error generating Quotation:', error);
      showError('Failed to generate Quotation. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const getFirstImageUrl = (item) => {
    let imageUrl = "";
    if (item.product_image) {
      imageUrl = item.product_image;
      if (!imageUrl.startsWith("http")) {
        imageUrl = API_BASE + imageUrl.replace(/^\/+/, "");
      }
    }
    return imageUrl;
  };

  if (isLoading) {
    return (
      <div style={{
        background: "#fff",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 40,
            height: 40,
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #333",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px"
          }}></div>
          <div style={{ color: "#666" }}>Loading quotation...</div>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 20px",
          textAlign: "center"
        }}>
          <div style={{
            fontSize: 48,
            marginBottom: 16,
            opacity: 0.3
          }}>
            ❌
          </div>
          <div style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 8
          }}>
            Quotation not found
          </div>
          <div style={{
            fontSize: 14,
            color: "#666",
            marginBottom: 24
          }}>
            The quotation you're looking for doesn't exist
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              background: "#333",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        padding: "18px 10px 0 10px",
        background: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 9
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 6px 10px 6px", marginBottom: 8
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <button
              onClick={() => navigate('/')}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginRight: 10,
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
              }}
              aria-label="Back"
            >
              <FaArrowLeft size={20} color="#222" />
            </button>
            <div style={{
              fontWeight: 700,
              fontSize: 21,
              color: "#232323",
              letterSpacing: "0.2px",
              marginTop: 2
            }}>
              Quotation Confirmation
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 16px" }}>
        {/* Success Message */}
        <div style={{
          background: "#d4edda",
          border: "1px solid #c3e6cb",
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          gap: 12
        }}>
          <FaCheckCircle size={20} color="#155724" />
          <div>
            <div style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#155724",
              marginBottom: 4
            }}>
              Quotation Created Successfully!
            </div>
            <div style={{
              fontSize: 14,
              color: "#155724"
            }}>
              Your quotation has been generated and is ready for download.
            </div>
          </div>
        </div>

        {/* Quotation Details */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          border: "1px solid #e9ecef"
        }}>
          <div style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Quotation Details
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span style={{ fontSize: 14, color: "#666" }}>Quotation Number:</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                {quotation.quotation_number}
              </span>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span style={{ fontSize: 14, color: "#666" }}>Customer:</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                {quotation.customer_name}
              </span>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span style={{ fontSize: 14, color: "#666" }}>Email:</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                {quotation.customer_email}
              </span>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span style={{ fontSize: 14, color: "#666" }}>Phone:</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                {quotation.customer_phone}
              </span>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span style={{ fontSize: 14, color: "#666" }}>Valid Until:</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                {new Date(quotation.valid_until).toLocaleDateString()}
              </span>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 8,
              borderTop: "1px solid #e9ecef",
              marginTop: 8
            }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>Total Amount:</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#333" }}>
                {formatPrice(quotation.total_amount)}
              </span>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Quotation Items ({quotation.items?.length || 0})
          </h3>

          {quotation.items?.map((item, index) => {
            const imageUrl = getFirstImageUrl(item);
            return (
              <div key={index} style={{
                background: "#fff",
                borderRadius: 12,
                padding: 16,
                marginBottom: 12,
                border: "1px solid #e9ecef",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12
                }}>
                  {/* Product Image */}
                  <div style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#eee",
                    flexShrink: 0
                  }}>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={item.product_name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    ) : (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: "#bbb",
                        fontSize: 12
                      }}>
                        No Image
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#333",
                      marginBottom: 4
                    }}>
                      {item.product_name}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: "#666",
                      marginBottom: 4
                    }}>
                      {formatPrice(item.unit_price)} each
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: "#666"
                    }}>
                      Quantity: {item.quantity}
                    </div>
                  </div>

                  {/* Total Price */}
                  <div style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#333"
                  }}>
                    {formatPrice(item.total_price)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Download Button */}
        <div style={{
          background: "#fff",
          borderRadius: 12,
          padding: 20,
          border: "1px solid #e9ecef",
          textAlign: "center"
        }}>
          {quotation && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              style={{
                background: downloading ? "#ccc" : "#000",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "12px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: downloading ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                justifyContent: "center"
              }}
            >
              <FaDownload size={16} />
              {downloading ? "Generating..." : "Download"}
            </button>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default QuotationConfirmationPage;
